import { XIcon } from '@heroicons/react/outline';
import { Modal as AntModal } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { UpgradeToPremiumImage } from 'assets';
import BookSubIcon from 'assets/Icons/BookSubIcon';
import CrownIcon from 'assets/Icons/CrownIcon';
import MobileIcon from 'assets/Icons/MobileIcon';
import TicketIcon from 'assets/Icons/TicketIcon';
import { motion } from 'framer-motion';
import Cookies from 'js-cookie';
import moment from 'moment';
import Image from 'next/image';
import { useRouter } from 'next/router';
import type React from 'react';
import { useEffect } from 'react';
import { useRecoilState } from 'recoil';

import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';

import { upgradeToPremiumModalState } from '@/atoms/UpgradeToPremiumModalState';

const COOKIE_VISITED_AT = 'premiumVisitedAt';
const SHOW_AFTER_DAYS = 3;

interface PremiumModalContentProps {
  onClose: () => void;
  handleRedirect: () => void;
}

const PremiumModalContent: React.FC<PremiumModalContentProps> = ({
  onClose,
  handleRedirect,
}) => {
  const screens = useBreakpoint();
  const features = [
    {
      icon: <MobileIcon />,
      text: (
        <>
          Get{' '}
          <span className='inline font-medium text-white'>
            3 - 12 Digital Books
          </span>{' '}
          depending on your plan
        </>
      ),
    },
    {
      icon: <BookSubIcon />,
      text: (
        <>
          Premium plan includes{' '}
          <span className='inline font-medium text-white'>
            12 Digital + Hardcover
          </span>{' '}
          Books
        </>
      ),
    },
    {
      icon: <TicketIcon />,
      text: (
        <>
          <span className='inline font-medium text-white'>
            Save up to 30% extra
          </span>{' '}
          with the best-value Premium plan
        </>
      ),
    },
  ];

  const animationVariant = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.15, duration: 0.4, ease: 'easeOut' },
    }),
  };

  return (
    <div className='flex text-white font-sans shadow-2xl w-[358px] lg:w-[1000px] lg:max-w-[98vw] md:rounded-xl md:flex-row h-full flex-col overflow-y-auto md:overflow-hidden'>
      <div className='w-full lg:w-2/5 p-4 xxxs:p-8 bg-[#151515] flex flex-col'>
        <div className='flex flex-col-reverse lg:flex-row items-center mb-2 gap-x-2'>
          <h2 className='text-2xl font-semibold text-white'>
            Upgrade To Premium
          </h2>
          <CrownIcon className='lg:w-7 lg:h-7 w-16 h-16' />
        </div>
        <p className='text-xs text-center lg:text-start md:text-sm text-zinc-400 mb-6 md:mb-10'>
          Get an extra 30% off when you subscribe to the Premium Plan—more
          value, more benefits!
        </p>
        <h3 className='text-white text-sm text-center lg:text-start font-medium leading-[1.375rem] mb-3'>
          Here's what you get by subscribing:
        </h3>
        <div className='space-y-2.5 mb-auto'>
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className='flex items-start gap-3 self-stretch p-[15px] rounded-lg border border-[#3c3b3e]'
              custom={index}
              initial='hidden'
              whileInView='visible'
              viewport={{ once: true, amount: 0.2 }}
              variants={animationVariant}
            >
              {feature.icon}
              <div className='text-textLightGray text-sm font-light leading-[1.375rem]'>
                {feature.text}
              </div>
            </motion.div>
          ))}
        </div>
        <ButtonDefault
          size={screens.lg ? 4 : 1}
          variant={ButtonVariants.PRIMARY}
          className='w-full lg:p-2 mt-6 xxxs:mt-10 lg:mt-0'
          onClick={() => {
            handleRedirect();
          }}
        >
          <span className='py-[1px] lg:py-[3px] text-base font-semibold whitespace-nowrap'>
            View Plans
          </span>
        </ButtonDefault>
      </div>
      <ShouldRender check={screens.lg}>
        <div className='w-full md:w-3/5  bg-[#2D2D2D] flex flex-col relative'>
          <button
            onClick={onClose}
            aria-label='Close modal'
            className='absolute top-4 right-4 text-white hover:text-white transition-colors z-10'
          >
            <XIcon className='w-5 h-5' />
          </button>
          <div className='flex flex-col md:rounded-xl md:flex-row md:items-center justify-between'>
            <Image
              src={UpgradeToPremiumImage}
              alt='UpgradeToPremiumImage'
              width={1200}
              height={1300}
              className='md:rounded-tr-xl md:rounded-br-xl'
            />
          </div>
        </div>
      </ShouldRender>
    </div>
  );
};

const UpgradeToPremiumModal = () => {
  const [isModalVisible, setIsModalVisible] = useRecoilState(
    upgradeToPremiumModalState,
  );
  const screens = useBreakpoint();
  const router = useRouter();

  const handleCancel = () => {
    Cookies.set(COOKIE_VISITED_AT, moment().toISOString(), { expires: 90 });
    sessionStorage.setItem('premiumModalDismissed', 'true');
    setIsModalVisible(false);
  };

  const handleRedirect = () => {
    router.push('/subscription/');
    handleCancel();
  };

  useEffect(() => {
    if (router.pathname !== '/') return setIsModalVisible(false);

    const lastVisit = Cookies.get(COOKIE_VISITED_AT);
    const dismissedThisSession = sessionStorage.getItem(
      'premiumModalDismissed',
    );

    if (!lastVisit) {
      Cookies.set(COOKIE_VISITED_AT, moment().toISOString(), { expires: 90 });
      return;
    }

    const daysPassed = moment().diff(moment(lastVisit), 'days');

    if (daysPassed >= SHOW_AFTER_DAYS && !dismissedThisSession) {
      setIsModalVisible(true);
    }
  }, [router.pathname, screens]);

  return (
    <AntModal
      open={isModalVisible}
      onCancel={handleCancel}
      footer={null}
      centered
      closable={screens.lg ? false : true}
      width='auto'
      maskClosable={true}
      className='m-0 premium-modal'
      classNames={{
        content: 'p-0 w-full md:rounded-xl',
      }}
      destroyOnHidden
      wrapClassName=' p-0 premium-modal'
    >
      <PremiumModalContent
        onClose={handleCancel}
        handleRedirect={handleRedirect}
      />
    </AntModal>
  );
};

export default UpgradeToPremiumModal;
